import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import {
  HelpCircle,
  Mail,
  Edit,
  Calendar,
  FileText,
  Lightbulb,
  GitBranch,
  Plus,
  CheckCircle,
  Layout,
  Code,
  Upload,
  FileOutput,
  Settings,
  ChevronRight,
  X
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { HELP_CONTENT } from './HelpContent';

// Icon mapping
const iconMap = {
  Mail,
  Edit,
  Calendar,
  FileText,
  Lightbulb,
  GitBranch,
  Plus,
  CheckCircle,
  Layout,
  Code,
  Upload,
  FileOutput,
  Settings
};

const getIcon = (iconName) => {
  const Icon = iconMap[iconName] || HelpCircle;
  return Icon;
};

// Step component
const HelpStep = ({ step, isLast }) => (
  <div className="flex gap-4">
    <div className="flex flex-col items-center">
      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
        {step.step}
      </div>
      {!isLast && <div className="w-0.5 h-full bg-border mt-2" />}
    </div>
    <div className="pb-6 flex-1">
      <h4 className="font-semibold text-sm mb-1">{step.title}</h4>
      <p className="text-sm text-muted-foreground whitespace-pre-line">
        {step.description}
      </p>
    </div>
  </div>
);

// Tips component
const HelpTips = ({ tips }) => (
  <div className="space-y-2">
    {tips.map((tip, index) => (
      <div key={index} className="flex gap-2 items-start">
        <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Lightbulb className="w-3 h-3 text-amber-600 dark:text-amber-400" />
        </div>
        <p className="text-sm text-muted-foreground">{tip}</p>
      </div>
    ))}
  </div>
);

// Section component
const HelpSection = ({ section, defaultOpen = false }) => {
  const Icon = getIcon(section.icon);
  
  return (
    <AccordionItem value={section.title} className="border rounded-lg px-4 mb-2">
      <AccordionTrigger className="hover:no-underline py-3">
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center",
            section.icon === 'Lightbulb' 
              ? "bg-amber-100 dark:bg-amber-900/30" 
              : "bg-primary/10"
          )}>
            <Icon className={cn(
              "w-4 h-4",
              section.icon === 'Lightbulb'
                ? "text-amber-600 dark:text-amber-400"
                : "text-primary"
            )} />
          </div>
          <span className="font-medium text-left">{section.title}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pt-2 pb-4">
        {section.steps && (
          <div className="mt-2">
            {section.steps.map((step, index) => (
              <HelpStep 
                key={step.step} 
                step={step} 
                isLast={index === section.steps.length - 1}
              />
            ))}
          </div>
        )}
        {section.tips && <HelpTips tips={section.tips} />}
      </AccordionContent>
    </AccordionItem>
  );
};

// Main Help Modal
export const HelpModal = ({ open, onClose, topic }) => {
  const content = HELP_CONTENT[topic];
  const [expandedSections, setExpandedSections] = useState([]);
  
  if (!content) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0" aria-describedby="help-modal-description">
        <DialogHeader className="p-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">{content.title}</DialogTitle>
                <p id="help-modal-description" className="text-sm text-muted-foreground mt-0.5">
                  {content.description}
                </p>
              </div>
            </div>
          </div>
        </DialogHeader>
        
        <ScrollArea className="h-[calc(85vh-140px)]">
          <div className="p-6">
            <Accordion 
              type="multiple" 
              value={expandedSections}
              onValueChange={setExpandedSections}
              className="space-y-2"
            >
              {content.sections.map((section, index) => (
                <HelpSection 
                  key={section.title} 
                  section={section}
                  defaultOpen={index === 0}
                />
              ))}
            </Accordion>
            
            {/* Quick expand all / collapse all */}
            <div className="flex justify-center gap-2 mt-4 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpandedSections(content.sections.map(s => s.title))}
              >
                Expand All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpandedSections([])}
              >
                Collapse All
              </Button>
            </div>
          </div>
        </ScrollArea>
        
        {/* Footer with Sophie tip */}
        <div className="p-4 border-t bg-muted/30">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="secondary" className="gap-1">
              <span className="text-lg">✨</span>
              Tip
            </Badge>
            <span>Ask Sophie for help anytime! She knows all about {content.title.toLowerCase()}.</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Help Button component that can be placed anywhere
export const HelpButton = ({ topic, variant = "outline", size = "sm", className }) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const content = HELP_CONTENT[topic];
  
  if (!content) return null;
  
  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={cn("gap-2", className)}
        onClick={() => setHelpOpen(true)}
        data-testid={`help-button-${topic}`}
      >
        <HelpCircle className="w-4 h-4" />
        Help
      </Button>
      
      <HelpModal 
        open={helpOpen} 
        onClose={() => setHelpOpen(false)} 
        topic={topic}
      />
    </>
  );
};

export default HelpModal;
